import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormInstance } from 'antd';

import {
  Form,
  ICONS,
  PLATFORM_THEME_COLORS,
  Icon,
  TextInput,
  Button,
  Typography,
  DeleteModal,
} from '@thinknet-gmbh/platform-common-ui';

import { useDeleteCompanyDepartmentById } from 'src/hooks/companies/delete-department-by-id.hook';

import {
  DepartmentsWrapper,
  FormItemsWrapper,
  FormListWrapper,
} from './departments-form-list.styles';

const DepartmentsList = ({
  parentField,
  parentLocationField,
  form,
}: {
  parentField: number;
  parentLocationField: number;
  form: FormInstance<any>;
}) => {
  const { t } = useTranslation(['settings', 'employee']);
  const [departmentToDelete, setDepartmentToDelete] = useState<any>(null);

  const deleteDepartment = useDeleteCompanyDepartmentById();

  return (
    <DepartmentsWrapper>
      <Typography weight="bold" fontColor={PLATFORM_THEME_COLORS.grayVariations.dark}>
        {t('settings:userProfileSettings.labels.department')}
      </Typography>
      <Form.List name={[parentField, 'departments']}>
        {(fields, { add, remove }) => {
          const deleteDepartmentHandler = (fieldName: number) => {
            const pathToFormData = [
              'locations',
              parentLocationField,
              'businessAddresses',
              parentField,
              'departments',
              fieldName,
            ];
            const departmentFormData = form.getFieldValue(pathToFormData);

            if (departmentFormData._id) {
              setDepartmentToDelete({ department: departmentFormData, fieldName });
            } else {
              remove(fieldName);
            }
          };

          const onSubmitDepartmentDelete = () => {
            deleteDepartment({
              variables: { departmentId: departmentToDelete?.department?._id },
              onCompleted: data => {
                if (data) {
                  remove(departmentToDelete?.fieldName);
                  setDepartmentToDelete(null);
                }
              },
            });
          };

          return (
            <FormListWrapper>
              <DeleteModal
                title={t('settings:company.deleteDepartmentModal.title')}
                subTitle={t('settings:company.deleteDepartmentModal.subtitle', {
                  department: departmentToDelete?.department?.name,
                })}
                onSubmit={() => onSubmitDepartmentDelete()}
                submitBtnText={t('common:buttons.delete')}
                translate={t}
                onClose={() => setDepartmentToDelete(null)}
                isOpened={!!departmentToDelete}
              />

              {fields.map(field => (
                <FormItemsWrapper key={field.key} isVisibleDelete={fields.length > 1}>
                  <div>
                    <Form.Item
                      name={[field.name, 'name']}
                      label={t('settings:userProfileSettings.labels.department')}
                      rules={[
                        {
                          required: true,
                          message: t('common:validation.error.departmentRequired'),
                        },
                      ]}
                    >
                      <TextInput
                        placeholder={t('settings:company.placeholders.finance')}
                        maxLength={100}
                      />
                    </Form.Item>

                    <Form.Item
                      name={[field.name, 'costCenter']}
                      label={t('employee:labels.costCenter')}
                    >
                      <TextInput
                        placeholder={t('settings:company.placeholders.financialDepartment')}
                        maxLength={30}
                      />
                    </Form.Item>

                    {field.name === fields.length - 1 && (
                      <Button
                        type="text"
                        icon={ICONS.plus}
                        iconColor={PLATFORM_THEME_COLORS.primary.blue}
                        onClick={() => add()}
                      >
                        {t('settings:company.buttons.addDepartment')}
                      </Button>
                    )}
                  </div>
                  {fields.length > 1 && (
                    <Icon
                      icon={ICONS.trash}
                      color={PLATFORM_THEME_COLORS.tone.red}
                      size={22}
                      rightMargin={false}
                      onClick={() => deleteDepartmentHandler(field.name)}
                    />
                  )}
                </FormItemsWrapper>
              ))}
            </FormListWrapper>
          );
        }}
      </Form.List>
    </DepartmentsWrapper>
  );
};

export default DepartmentsList;
